import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { InvocationStatusType } from '@/types/core/connector-invocation-status';
import type { Connector } from '@/types/core/connector';
import type { UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

import { RUN_HISTORY_COUNT } from '@/constants/run-history';

const RUN_COUNT = RUN_HISTORY_COUNT;

export interface ConnectorActivitySeries {
  connectorId: UUID;
  name: string;
  data: number[];
}

export interface ConnectorRunHealth {
  connectorId: UUID;
  name: string;
  /** Individual run outcomes aligned to run positions, oldest first */
  runs: InvocationStatusType[];
  passed: number;
  failed: number;
  other: number;
}

export interface ConnectorActivityData {
  labels: string[];
  series: ConnectorActivitySeries[];
  health: ConnectorRunHealth[];
  isLoading: boolean;
}

/**
 * For each connector, fetches the last {@link RUN_COUNT} invocations of every
 * collector feature and aggregates entitiesCollected by relative run position.
 *
 * Position 0 = oldest shown run, position 9 = most recent.
 * Each feature is aligned independently — feature A on run 200 and feature B
 * on run 300 will both appear at position 9 (their own most recent).
 */
export function useConnectorActivityBuckets(
  tenantId: UUID | undefined,
  connectors: Connector[]
): ConnectorActivityData {
  // Step 1: collector features per connector — reuses configurationStatus cache
  const featureResults = useQueries({
    queries: connectors.map((c) => ({
      queryKey: connectorQueryKeys.configurationStatus(tenantId, c.id),
      queryFn: () =>
        connectorService
          .listConnectorFeatures(tenantId!, c.id, { limit: 100 })
          .then((page) => {
            // If the read model hasn't propagated yet, throw so React Query
            // retries instead of caching an empty result that leaves the chart blank.
            if (page.items.length === 0) {
              throw new Error('Empty features response, retrying');
            }
            return page;
          }),
      enabled: !!tenantId && connectors.length > 0,
      retry: 3,
      retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
    })),
  });

  const featuresLoaded = featureResults.every((r) => !r.isLoading);

  // Flatten to (connectorId, featureId) pairs for collector features only
  const featurePairs = useMemo<{ connectorId: UUID; featureId: UUID }[]>(() => {
    if (!featuresLoaded) return [];
    const pairs: { connectorId: UUID; featureId: UUID }[] = [];
    connectors.forEach((c, i) => {
      const items = featureResults[i]?.data?.items ?? [];
      for (const f of items) {
        if (f.schedulable !== false) {
          pairs.push({ connectorId: c.id, featureId: f.featureId });
        }
      }
    });
    return pairs;
  }, [connectors, featureResults, featuresLoaded]);

  // Step 2: recent invocations per collector feature (newest first)
  const invocationResults = useQueries({
    queries: featurePairs.map(({ connectorId, featureId }) => ({
      queryKey: connectorQueryKeys.invocationStatuses(
        tenantId,
        connectorId,
        featureId,
        RUN_COUNT + 1
      ),
      queryFn: () =>
        connectorService.listConnectorInvocationStatuses(
          tenantId!,
          connectorId,
          featureId,
          { limit: RUN_COUNT + 1 }
        ),
      enabled: !!tenantId && featuresLoaded,
      refetchInterval: 15_000,
    })),
  });

  const isLoading =
    featureResults.some((r) => r.isLoading) ||
    invocationResults.some((r) => r.isLoading);

  const { labels, series, health } = useMemo(() => {
    // Labels: run positions from oldest (left) to newest (right)
    const runLabels = Array.from({ length: RUN_COUNT }, (_, i) =>
      String(i + 1)
    );

    // For each connector, aggregate entities and statuses by position across all its features
    const connectorData = new Map<UUID, number[]>(
      connectors.map((c) => [c.id, new Array<number>(RUN_COUNT).fill(0)])
    );
    // Health: per-position status votes per connector (majority outcome per position)
    const connectorStatusVotes = new Map<
      UUID,
      { failed: number; passed: number }[]
    >(
      connectors.map((c) => [
        c.id,
        Array.from({ length: RUN_COUNT }, () => ({ failed: 0, passed: 0 })),
      ])
    );

    featurePairs.forEach(({ connectorId }, idx) => {
      const runs = invocationResults[idx]?.data?.items;
      if (!runs || runs.length === 0) return;

      // Runs come newest-first from the API; take up to RUN_COUNT
      const recent = runs.slice(1, RUN_COUNT + 1);

      // Align to the RIGHT side of the array so newest is always at position RUN_COUNT-1
      const offset = RUN_COUNT - recent.length;
      for (let i = recent.length - 1; i >= 0; i--) {
        const run = recent[i];
        const count =
          run.metrics?.collection?.entitiesCollected ?? run.processedCount ?? 0;
        const pos = offset + (recent.length - 1 - i);
        connectorData.get(connectorId)![pos] += count;
        const votes = connectorStatusVotes.get(connectorId)![pos];
        if (run.status === 'failed') {
          votes.failed++;
        } else if (run.status === 'completed') {
          votes.passed++;
        }
      }
    });

    const computedSeries: ConnectorActivitySeries[] = connectors.map((c) => ({
      connectorId: c.id,
      name: c.name ?? c.id,
      data: connectorData.get(c.id) ?? new Array<number>(RUN_COUNT).fill(0),
    }));

    const computedHealth: ConnectorRunHealth[] = connectors.map((c) => {
      const votes = connectorStatusVotes.get(c.id) ?? [];
      const runs: InvocationStatusType[] = votes.map((v) => {
        if (v.failed > 0 && v.passed === 0) return 'failed';
        if (v.passed > 0) return 'completed';
        return 'queued'; // placeholder for no data
      });
      const passed = votes.filter((v) => v.passed > 0 && v.failed === 0).length;
      const failed = votes.filter((v) => v.failed > 0).length;
      const other = RUN_COUNT - passed - failed;
      return {
        connectorId: c.id,
        name: c.name ?? c.id,
        runs,
        passed,
        failed,
        other,
      };
    });

    return {
      labels: runLabels,
      series: computedSeries,
      health: computedHealth,
    };
  }, [connectors, featurePairs, invocationResults]);

  return { labels, series, health, isLoading };
}
